import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import {
  BELOW,
  comparisons,
  cyclesText,
  dependencyReport,
  enabledText,
  IMPORTS,
  importSpec,
  initialImports,
  initialRefactor,
  initialScope,
  initialSort,
  LAUNCH_TASKS,
  LAYERS,
  layerFiles,
  layerLabels,
  layerQuestions,
  LINE_COUNT,
  linesOf,
  MOVE_COUNT,
  PLANS,
  planLabels,
  refactorFinished,
  refactorMessage,
  refactorRows,
  refactorTransition,
  runLabels,
  SCOPE_PROMPT,
  SCOPE_RUNS,
  scopeLabels,
  scopeOutcome,
  SCOPES,
  scopeTransition,
  sortReport,
  sortTransition,
  STACK,
  suspects,
  type ImportId,
  type ImportSet,
  type Layer,
  type Module,
  type Plan,
  type RefactorAction,
  type Scope,
  type ScopeAction,
  type SortAction,
} from './layer-models';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './layer-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
  className = '',
  empty = 'No rows',
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
  className?: string;
  empty?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className={`rel-table cb-record-grid ${className}`} role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">{empty}</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const SIMULATION = "A simulation of CollabBoard's code on the lesson data.";
const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
const layerOptions = LAYERS.map((layer) => ({ value: layer, label: layerLabels[layer] }));

export function LayerSortLab() {
  const [state, setState] = useState(initialSort);
  const act = (action: SortAction) => setState((current) => sortTransition(current, action));
  const report = sortReport(state);
  return (
    <Lab
      title="Which part should own this line?"
      eyebrow="Layers · one job each"
      className="rel-lab layer-lab"
    >
      <Predict>
        Each line below comes from CollabBoard&apos;s code. Ask which question it answers: what HTTP
        says, whether something is allowed, or which rows. Predict its part before you press Check.
      </Predict>
      <ol className="layer-sort" aria-label="Lines to place">
        {report.results.map((result) => (
          <li key={result.line.id} data-verdict={state.checked ? result.verdict : undefined}>
            <span className="layer-sort-number" aria-hidden="true">
              {result.number}
            </span>
            <code>{result.line.code}</code>
            <ModelSelect
              aria-label={`Line ${result.number}: ${result.line.code}`}
              value={result.answer}
              placeholder="Choose a part"
              onValueChange={(value) =>
                act({ kind: 'answer', line: result.line.id, layer: value as Layer })
              }
              options={layerOptions}
            />
            {state.checked ? <p>{result.text}</p> : null}
          </li>
        ))}
      </ol>
      <div className="cb-controls">
        <Button size="sm" onClick={() => act({ kind: 'check' })}>
          Check
        </Button>
        <Reset onClick={() => act({ kind: 'reset' })} />
      </div>
      <Facts
        values={[
          ['Answered', `${report.answered} of ${LINE_COUNT}`],
          ['Right', state.checked ? `${report.right} of ${LINE_COUNT}` : '(not checked yet)'],
          [
            'Wrong placements',
            state.checked
              ? report.wrong
                  .map(
                    (result) =>
                      `line ${result.number} in ${result.answer ? layerLabels[result.answer] : ''}`,
                  )
                  .join('; ') || 'none'
              : '(not checked yet)',
          ],
        ]}
      />
      {report.solved ? (
        <div className="layer-parts">
          {LAYERS.map((layer) => (
            <Panel key={layer} label={`${layerLabels[layer]} · ${layerFiles[layer]}`}>
              <p>Answers {layerQuestions[layer]}</p>
              <pre aria-label={`${layerLabels[layer]} lines`}>{linesOf(layer).join('\n')}</pre>
            </Panel>
          ))}
        </div>
      ) : null}
      <Result tone={report.tone}>{report.message}</Result>
      <p className="rel-caption">
        {SIMULATION} Changing a line clears the check; press Check again to see the new verdicts.
      </p>
    </Lab>
  );
}

function ModuleBox({ module, cycle }: { module: Module; cycle: boolean }) {
  return (
    <div className="layer-module" data-cycle={cycle ? 'true' : undefined}>
      <strong>{layerFiles[module]}</strong>
      <small>{sentence(layerQuestions[module])}</small>
    </div>
  );
}
function Arrows({ on, ids }: { on: ImportSet; ids: ImportId[] }) {
  const shown = ids.filter((id) => on[id]).map(importSpec);
  return (
    <div className="layer-gap">
      {shown.map((entry) => (
        <span key={entry.id} data-kind={entry.kind}>
          {entry.kind === 'backward' ? '↑' : '↓'} {entry.label}
        </span>
      ))}
    </div>
  );
}

export function DependencyDirectionLab() {
  const [on, setOn] = useState<ImportSet>(initialImports);
  const report = dependencyReport(on);
  const cycled = (module: Module) => report.cycles.some((cycle) => cycle.includes(module));
  return (
    <Lab
      title="Which way may imports point?"
      eyebrow="Layers · dependency direction"
      className="rel-lab layer-lab"
    >
      <Predict>
        Routes import services and services import the repository. Predict what goes wrong when an
        import points up the stack, and when a route reaches past services to the repository.
      </Predict>
      <fieldset className="layer-imports">
        <legend>Imports</legend>
        {IMPORTS.map((entry) => (
          <label className="cb-check" key={entry.id}>
            <input
              type="checkbox"
              checked={on[entry.id]}
              onChange={() => setOn((current) => ({ ...current, [entry.id]: !current[entry.id] }))}
            />
            {entry.label}
          </label>
        ))}
      </fieldset>
      <div className="cb-controls">
        <Reset onClick={() => setOn(initialImports())} />
      </div>
      <div className="layer-stack" role="group" aria-label="Modules, top to bottom">
        <div className="layer-stack-main">
          <ModuleBox module="routes" cycle={cycled('routes')} />
          <Arrows on={on} ids={['routes-services', 'services-routes']} />
          <ModuleBox module="services" cycle={cycled('services')} />
          <Arrows on={on} ids={['services-repository', 'repository-services']} />
          <ModuleBox module="repository" cycle={cycled('repository')} />
        </div>
        <div className="layer-skip" data-on={on['routes-repository'] ? 'true' : undefined}>
          {on['routes-repository'] ? <span>↓ {importSpec('routes-repository').label}</span> : null}
        </div>
        <div className="layer-below">
          <small>Below every layer; they import none of the three</small>
          <div>
            {BELOW.map((file) => (
              <span key={file}>{file}</span>
            ))}
          </div>
        </div>
      </div>
      <Facts
        values={[
          ['Imports on', enabledText(report)],
          ['Cycles', cyclesText(report)],
          [
            'Pointing up or skipping a layer',
            report.problems.map((entry) => entry.label).join('; ') || 'none',
          ],
        ]}
      />
      <Result tone={report.tone}>{report.message}</Result>
      <p className="rel-caption">
        A simulation of the imports between CollabBoard&apos;s modules. The stack reads top to
        bottom: {STACK.map((module) => layerFiles[module]).join(', ')}, then {BELOW.join(', ')}.
      </p>
    </Lab>
  );
}

export function ConnectionScopeLab() {
  const [state, setState] = useState(initialScope);
  const act = (action: ScopeAction) => setState((current) => scopeTransition(current, action));
  const outcome = state.run ? scopeOutcome(state.scope, state.run) : null;
  const tasks = outcome ? outcome.tasksAfter : LAUNCH_TASKS;
  return (
    <Lab
      title="What can a test undo?"
      eyebrow="Layers · one connection per request"
      className="rel-lab layer-lab"
    >
      <Predict>
        Run Ravi&apos;s delete and the test under each scope. Predict how many transactions each run
        uses, and whether the test&apos;s task is still in the table after its rollback.
      </Predict>
      <div className="cb-controls">
        <label>
          Scope
          <ModelSelect
            aria-label="Scope"
            value={state.scope}
            onValueChange={(value) => act({ kind: 'set-scope', scope: value as Scope })}
            options={SCOPES.map((key) => ({ value: key, label: scopeLabels[key] }))}
          />
        </label>
      </div>
      <div className="cb-controls">
        {SCOPE_RUNS.map((run, index) => (
          <Button
            key={run}
            size="sm"
            variant={index === 0 ? 'primary' : 'secondary'}
            onClick={() => act({ kind: 'run', run })}
          >
            {runLabels[run]}
          </Button>
        ))}
        <Reset onClick={() => act({ kind: 'reset' })} />
      </div>
      <Table
        label="Timeline"
        columns={['#', 'Transaction', 'Step']}
        template="minmax(0, 0.2fr) minmax(0, 0.9fr) minmax(0, 2fr)"
        className="layer-wide"
        empty="Nothing run yet"
        rows={(outcome?.timeline ?? []).map((entry, index) => [
          index + 1,
          entry.transaction,
          entry.step,
        ])}
      />
      <Table
        label="Apollo's tasks after the run"
        columns={['id', 'title']}
        template="minmax(0, 0.3fr) minmax(0, 1.7fr)"
        rows={tasks.map((task) => [task.id, task.title])}
      />
      <Facts
        values={[
          ['Scope', scopeLabels[state.scope]],
          ['Transactions', outcome ? String(outcome.transactions) : '(nothing run yet)'],
          ['Commits', outcome ? String(outcome.commits) : '(nothing run yet)'],
          ...(outcome ? [outcome.fact] : []),
        ]}
      />
      <Result tone={outcome?.tone ?? 'neutral'}>{outcome ? outcome.message : SCOPE_PROMPT}</Result>
      <p className="rel-caption">
        A simulation of CollabBoard&apos;s transactions on the lesson data. Before these runs, Maya
        made Ravi a manager of Apollo, so his role check passes. One transaction alone does not stop
        a demotion committed between the check and the delete. A lock taken by the check would, but
        a lock lasts only until its transaction ends, so it covers the delete only when both run in
        one transaction.
      </p>
    </Lab>
  );
}

export function RefactorStepsLab() {
  const [state, setState] = useState(initialRefactor);
  const act = (action: RefactorAction) =>
    setState((current) => refactorTransition(current, action));
  const finished = refactorFinished(state);
  const { message, tone } = refactorMessage(state);
  return (
    <Lab
      title="Where is the mistake found?"
      eyebrow="Layers · small steps"
      className="rel-lab layer-lab"
    >
      <Predict>
        One of the five moves hides a mistake. Predict what each plan&apos;s comparison can tell you
        about which move made it.
      </Predict>
      <div className="cb-controls">
        <label>
          Plan
          <ModelSelect
            aria-label="Plan"
            value={state.plan}
            onValueChange={(value) => act({ kind: 'set-plan', plan: value as Plan })}
            options={PLANS.map((key) => ({ value: key, label: planLabels[key] }))}
          />
        </label>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={finished} onClick={() => act({ kind: 'next' })}>
          Next move
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={finished}
          onClick={() => act({ kind: 'run' })}
        >
          Run the plan
        </Button>
        <Reset onClick={() => act({ kind: 'reset' })} />
      </div>
      <Table
        label="Moves and comparisons"
        columns={['Move', 'Compare after it']}
        template="minmax(0, 1fr) minmax(0, 1.6fr)"
        className="layer-moves"
        rows={refactorRows(state)}
      />
      <Facts
        values={[
          ['Moves made', `${state.done} of ${MOVE_COUNT}`],
          ['Comparisons', String(comparisons(state))],
          ['Moves that could hold the mistake', suspects(state)],
        ]}
      />
      <Result tone={tone}>{message}</Result>
      <p className="rel-caption">
        A simulation of the refactor. Each comparison runs python3 behavior.py compare behavior.txt
        against the behavior recorded before move 1.
      </p>
    </Lab>
  );
}
